// @-mentions in the operator composer.
//
// Operators know customers by name and mistype them constantly ("Pozzolungo"),
// so typing "@" opens a search and inserts the chosen customer's name. What the
// operator READS stays human: "@Isabella Pozzolungo", no uuid in the box.
//
// But chat history is TEXT ONLY — tool calls and their results live for one
// request and are then discarded, so an id that never reaches the transcript is
// an id the model invents on a later turn (that is the fabricated-service_id
// outage the widget shipped). So at send time the chosen ids are appended to the
// message as ONE marker line, and the bubble hides that line again when it
// displays the message. Same split as the settle handoff: what the model
// receives and what the operator reads differ only in presentation, never in
// content.
//
// This module is pure so the format has exactly one definition — generator,
// detector and stripper live together and cannot drift apart.

export interface MentionedCustomer {
  id: string
  name: string
}

/** Longest query we keep the popup open for — past this it is prose, not a name. */
const MAX_QUERY_LENGTH = 32
/** Names run to two or three words; more spaces than this means the operator moved on. */
const MAX_QUERY_WORDS = 3

export interface ActiveMention {
  /** Text typed after the '@', which is what gets searched. */
  query: string
  /** Index of the '@' itself, so a chosen name can replace the whole token. */
  start: number
}

/**
 * The @-token the caret is currently sitting in, or null when no mention is
 * being typed. A mention only starts at the beginning of the message or after
 * whitespace, so an email address never opens the popup.
 */
export function activeMention(text: string, caret: number): ActiveMention | null {
  const upToCaret = text.slice(0, Math.max(0, Math.min(caret, text.length)))
  const at = upToCaret.lastIndexOf('@')
  if (at === -1) return null

  const before = at === 0 ? '' : upToCaret[at - 1]
  if (before && !/\s/.test(before)) return null

  const query = upToCaret.slice(at + 1)
  if (query.length > MAX_QUERY_LENGTH) return null
  if (query.includes('\n') || query.includes('@')) return null
  if (query.trim().split(/\s+/).filter(Boolean).length > MAX_QUERY_WORDS) return null

  return { query, start: at }
}

export interface MentionInsertion {
  text: string
  caret: number
}

/** Replace the open @-token with the chosen customer's name, plus a space. */
export function applyMention(
  text: string,
  mention: ActiveMention,
  caret: number,
  customer: MentionedCustomer,
): MentionInsertion {
  const token = `@${customer.name} `
  const next = text.slice(0, mention.start) + token + text.slice(caret)
  return { text: next, caret: mention.start + token.length }
}

const MARKER_RE = /\n*\[mentions: [^\n[\]]*\]\s*$/

/**
 * Which of the tracked mentions the operator actually left in the message. A
 * name that was typed and then deleted must not carry its id along, or the model
 * acts on a customer the operator removed.
 */
export function usedMentions(text: string, mentions: MentionedCustomer[]): MentionedCustomer[] {
  const seen = new Set<string>()
  return mentions.filter((m) => {
    if (seen.has(m.id) || !m.name) return false
    if (!text.includes(`@${m.name}`)) return false
    seen.add(m.id)
    return true
  })
}

/**
 * Attach the ids of every mention still in the message. Returns the text
 * unchanged when nothing is mentioned, so an ordinary message never grows a
 * marker.
 */
export function appendMentionContext(text: string, mentions: MentionedCustomer[]): string {
  const used = usedMentions(text, mentions)
  if (used.length === 0) return text
  const pairs = used.map((m) => `${m.name} = ${m.id}`).join('; ')
  return `${text}\n\n[mentions: ${pairs}]`
}

/** Display form: the same message without the machine-readable id line. */
export function stripMentionContext(text: string): string {
  return text.replace(MARKER_RE, '')
}

export function hasMentionContext(text: string): boolean {
  return MARKER_RE.test(text)
}
